// ─── Central Business Rule Helpers ───────────────────────────────────────────
// All permission and read-only checks live here.
// Import and reuse everywhere — never duplicate these checks in components.

/** True when the task/subtask status is "closed" (case-insensitive). */
export const isTaskClosed = (task) =>
  (task?.status || '').toLowerCase() === 'closed';

/** A task is read-only when it is closed. */
export const isTaskReadOnly = (task) => isTaskClosed(task);

/** Can the user edit/mutate this task? */
export const canEditTask = (user, task) => {
  if (!user || !task) return false;
  if (isTaskReadOnly(task)) return false;
  const role = (user.role || '').toLowerCase();
  if (role === 'admin' || role === 'superuser' || role === 'manager') return true;
  const uid = user.userId || user.id;
  return Number(task.assigned_to) === Number(uid);
};

/** Can the user change the status of this task? */
export const canChangeStatus = (user, task) => canEditTask(user, task);

/**
 * Can the user close this main task?
 * All main tasks given to employees can only be closed by:
 * - Manager (for project tasks)
 * - Admin / Superuser (for admin tasks and management requests)
 * Employees can NEVER close any main task.
 */
export const canCloseTask = (user, task) => {
  if (!user || !task) return false;
  if (isTaskReadOnly(task)) return false;
  const role = (user.role || '').toLowerCase();
  if (role === 'employee') return false;

  const code = (task.task_type_code || task.task_type || '').toUpperCase();
  if (code === 'PROJECT') {
    return role === 'manager';
  }
  if (code === 'ADMIN_TASK' || code === 'ADMIN_REQUEST') {
    return role === 'admin' || role === 'superuser';
  }
  // Fallback for general main tasks: manager or admin/superuser only
  return role === 'admin' || role === 'superuser' || role === 'manager';
};

/** Can the user assign/reassign this task? */
export const canAssignTask = (user, task) => {
  if (!user || !task) return false;
  if (isTaskReadOnly(task)) return false;
  const role = (user.role || '').toLowerCase();
  return role === 'admin' || role === 'superuser' || role === 'manager';
};

/** Can the user create a subtask under this parent task? */
export const canCreateSubtask = (user, task) => {
  if (!user || !task) return false;
  if (isTaskReadOnly(task)) return false;
  const role = (user.role || '').toLowerCase();
  if (role === 'admin' || role === 'superuser' || role === 'manager') return true;
  const uid = user.userId || user.id;
  return Number(task.assigned_to) === Number(uid);
};

/** Can the user edit a subtask? Also blocked when parent task is closed. */
export const canEditSubtask = (user, subtask, parentTask) => {
  if (!user || !subtask) return false;
  if (isTaskClosed(subtask)) return false;
  if (parentTask && isTaskClosed(parentTask)) return false;
  const role = (user.role || '').toLowerCase();
  if (role === 'admin' || role === 'superuser' || role === 'manager') return true;
  const uid = user.userId || user.id;
  return (
    Number(subtask.assigned_to) === Number(uid) ||
    (parentTask && Number(parentTask.assigned_to) === Number(uid))
  );
};

/**
 * Can the user change the status of a subtask?
 * The assignee of the subtask (or parent task) can change status only.
 * This is a superset of canFullyEditSubtask.
 */
export const canChangeSubtaskStatus = (user, subtask, parentTask) =>
  canEditSubtask(user, subtask, parentTask);

/**
 * Can the user fully edit a subtask (title, description, priority, due date)?
 * Only the manager and the subtask creator are allowed — NOT the assignee alone.
 */
export const canFullyEditSubtask = (user, subtask, parentTask) => {
  if (!user || !subtask) return false;
  if (isTaskClosed(subtask)) return false;
  if (parentTask && isTaskClosed(parentTask)) return false;
  const role = (user.role || '').toLowerCase();
  if (role === 'admin' || role === 'superuser' || role === 'manager') return true;
  const uid = user.userId || user.id;
  // Only the subtask creator — NOT the assignee — can fully edit
  return Number(subtask.created_by) === Number(uid);
};

/** Can the user post a comment on this task? */
export const canComment = (user, task) => {
  if (!user || !task) return false;
  if (isTaskReadOnly(task)) return false;
  return true;
};

/**
 * Does this task type support subtasks?
 * ADMIN_TASK and ADMIN_REQUEST never have subtasks.
 * Only PROJECT tasks do.
 */
export const supportsSubtasks = (task) => {
  if (!task) return false;
  const code = (task.task_type_code || task.task_type || '').toUpperCase();
  if (code === 'ADMIN_TASK' || code === 'ADMIN_REQUEST') return false;
  return true;
};

/** Can the user submit a report for this task? */
export const canSubmitReport = (user, task) => {
  if (!user || !task) return false;
  if (isTaskReadOnly(task)) return false;
  return true;
};

/**
 * What level of parent-task visibility does this user have inside the Subtask Drawer?
 * Returns: 'button' | 'compact' | 'none'
 *
 * none    — manager, admin, superuser (already on parent task page — no duplication)
 * button  — employee who is the parent-task assignee (has full access, show link only)
 * compact — subtask-only employee (show title/status/priority/description only)
 */
export const parentTaskVisibility = (user, parentTask) => {
  if (!user || !parentTask) return 'none';
  const role = (user.role || '').toLowerCase();
  if (role === 'admin' || role === 'superuser' || role === 'manager') return 'none';
  const uid = user.userId || user.id;
  if (Number(parentTask.assigned_to) === Number(uid)) return 'button';
  return 'compact';
};
